import { computed, ref } from 'vue';

export const useAdminSchedule = ({
  updateConfig,
  createDefaultWeeklyTemplates,
  weeklyDayLabelsBase,
  getBaseTimesForDay,
  getAdminNow
}) => {
  const disabledTimes = ref({});
  const selectedDate = ref('');
  const showScheduleModal = ref(false);
  const showCalendarModal = ref(false);
  const scheduleTab = ref('base');
  const selectedWeeklyDay = ref(Object.keys(weeklyDayLabelsBase)[0] || '');
  const weeklyTemplates = ref(createDefaultWeeklyTemplates());
  const weeklyDayLabels = ref({ ...weeklyDayLabelsBase });
  const customWeeklyTime = ref('');
  const newDayLabel = ref('');
  const editingDayKey = ref(null);
  const editingDayLabel = ref('');
  const editingWeeklyTime = ref(null);
  const editingWeeklyTimeValue = ref('');

  const cloneData = (value) => JSON.parse(JSON.stringify(value));

  const saveScheduleConfig = async () => {
    await updateConfig({
      weeklyTemplates: cloneData(weeklyTemplates.value),
      weeklyDayLabels: cloneData(weeklyDayLabels.value)
    });
  };

  const getDayLabel = (dayKey) => weeklyDayLabels.value[dayKey] || weeklyDayLabelsBase[dayKey] || dayKey;

  const slugifyDayLabel = (label) => label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const ensureSelectedWeeklyDay = () => {
    const keys = Object.keys(weeklyTemplates.value || {});
    if (!keys.length) return;
    if (!keys.includes(selectedWeeklyDay.value)) {
      [selectedWeeklyDay.value] = keys;
    }
  };

  const weeklyDayOptions = computed(() => {
    const templates = weeklyTemplates.value || {};
    const keys = Object.keys(templates);
    return keys.length ? keys : Object.keys(weeklyDayLabelsBase);
  });

  const selectWeeklyDay = (dayKey) => {
    selectedWeeklyDay.value = dayKey;
    editingDayKey.value = null;
    editingDayLabel.value = '';
  };

  const addWeeklyDay = async () => {
    const label = newDayLabel.value.trim();
    if (!label) {
      alert('Escribe un nombre para el nuevo día.');
      return;
    }
    const baseSlug = slugifyDayLabel(label) || `dia-${Date.now()}`;
    let slug = baseSlug;
    let counter = 1;
    const templates = { ...weeklyTemplates.value };
    while (templates[slug]) {
      slug = `${baseSlug}-${counter++}`;
    }

    templates[slug] = [...getBaseTimesForDay(slug)];
    weeklyTemplates.value = templates;
    weeklyDayLabels.value = { ...weeklyDayLabels.value, [slug]: label };
    newDayLabel.value = '';
    selectedWeeklyDay.value = slug;
    await saveScheduleConfig();
  };

  const startEditDay = (dayKey) => {
    editingDayKey.value = dayKey;
    editingDayLabel.value = getDayLabel(dayKey);
  };

  const cancelEditDay = () => {
    editingDayKey.value = null;
    editingDayLabel.value = '';
  };

  const saveDayLabel = async () => {
    if (!editingDayKey.value) return;
    const label = editingDayLabel.value.trim();
    if (!label) {
      alert('El nombre no puede estar vacío.');
      return;
    }
    weeklyDayLabels.value = {
      ...weeklyDayLabels.value,
      [editingDayKey.value]: label
    };
    editingDayKey.value = null;
    editingDayLabel.value = '';
    await saveScheduleConfig();
  };

  const deleteWeeklyDay = async (dayKey) => {
    const keys = Object.keys(weeklyTemplates.value || {});
    if (keys.length <= 1) {
      alert('Debes mantener al menos un día configurado.');
      return;
    }
    const confirmDelete = window.confirm('¿Eliminar este día y todos sus horarios base?');
    if (!confirmDelete) return;

    const templates = { ...weeklyTemplates.value };
    delete templates[dayKey];
    weeklyTemplates.value = templates;

    const labels = { ...weeklyDayLabels.value };
    delete labels[dayKey];
    weeklyDayLabels.value = labels;

    if (selectedWeeklyDay.value === dayKey) {
      ensureSelectedWeeklyDay();
    }

    await saveScheduleConfig();
  };

  const startEditWeeklyTime = (dayKey, time) => {
    editingWeeklyTime.value = { dayKey, originalTime: time };
    editingWeeklyTimeValue.value = time;
  };

  const cancelEditWeeklyTime = () => {
    editingWeeklyTime.value = null;
    editingWeeklyTimeValue.value = '';
  };

  const saveWeeklyTimeEdit = async () => {
    const entry = editingWeeklyTime.value;
    if (!entry) return;
    const value = editingWeeklyTimeValue.value.trim();
    if (!/^([0-1]\d|2[0-3]):[0-5]\d$/.test(value)) {
      alert('Usa el formato HH:MM en 24 horas.');
      return;
    }
    const template = weeklyTemplates.value[entry.dayKey] ? [...weeklyTemplates.value[entry.dayKey]] : [];
    const index = template.findIndex((slot) => slot === entry.originalTime);
    if (index === -1) {
      alert('No se encontró el horario a editar.');
      return;
    }
    if (template.includes(value) && value !== entry.originalTime) {
      alert('Ese horario ya existe.');
      return;
    }
    template[index] = value;
    template.sort();
    weeklyTemplates.value = {
      ...weeklyTemplates.value,
      [entry.dayKey]: template
    };
    await saveScheduleConfig();
    cancelEditWeeklyTime();
  };

  const removeWeeklyTime = async (dayKey, time) => {
    const template = weeklyTemplates.value[dayKey] ? [...weeklyTemplates.value[dayKey]] : [];
    const updated = template.filter((slot) => slot !== time);
    weeklyTemplates.value = {
      ...weeklyTemplates.value,
      [dayKey]: updated
    };
    if (!updated.length) {
      alert('Este día quedó sin horarios; recuerda añadir nuevos si quieres mantenerlo visible.');
    }
    await saveScheduleConfig();
    if (editingWeeklyTime.value?.dayKey === dayKey && editingWeeklyTime.value?.originalTime === time) {
      cancelEditWeeklyTime();
    }
  };

  const isTimeDisabled = (date, time) => disabledTimes.value[date]?.includes(time);

  const toggleTime = async (date, time) => {
    if (!date) return;
    const current = disabledTimes.value[date] ? [...disabledTimes.value[date]] : [];
    const updatedTimes = current.includes(time)
      ? current.filter((slot) => slot !== time)
      : [...current, time];

    const updatedDisabledTimes = {
      ...disabledTimes.value,
      [date]: updatedTimes
    };

    disabledTimes.value = updatedDisabledTimes;
    await updateConfig({ disabledTimes: updatedDisabledTimes });
  };

  const addCustomWeeklyTime = async () => {
    const value = customWeeklyTime.value.trim();
    if (!/^([0-1]\d|2[0-3]):[0-5]\d$/.test(value)) {
      alert('Usa el formato HH:MM en 24 horas.');
      return;
    }

    const dayKey = selectedWeeklyDay.value;
    if (!dayKey) {
      alert('Selecciona un día antes de añadir un horario.');
      return;
    }
    if (weeklyTemplates.value[dayKey]?.includes(value)) {
      alert('Ese horario ya existe para este día.');
      return;
    }

    const updated = {
      ...weeklyTemplates.value,
      [dayKey]: [...(weeklyTemplates.value[dayKey] || []), value].sort()
    };

    weeklyTemplates.value = updated;
    customWeeklyTime.value = '';
    await saveScheduleConfig();
  };

  const weekendDayIndex = {
    friday: 5,
    saturday: 6,
    sunday: 0
  };

  const jumpToNextWeekend = (dayKey) => {
    const targetIndex = weekendDayIndex[dayKey];
    if (targetIndex === undefined) return;
    const baseDate = getAdminNow();
    const currentIndex = baseDate.getDay();
    let diff = (targetIndex - currentIndex + 7) % 7;
    if (diff === 0) diff = 7;
    baseDate.setDate(baseDate.getDate() + diff);
    selectedDate.value = `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, '0')}-${String(baseDate.getDate()).padStart(2, '0')}`;
    loadTimesForDate();
  };

  const loadTimesForDate = () => {
    if (selectedDate.value && !disabledTimes.value[selectedDate.value]) {
      disabledTimes.value[selectedDate.value] = [];
    }
  };

  const openScheduleModal = () => {
    showScheduleModal.value = true;
  };

  const closeScheduleModal = () => {
    showScheduleModal.value = false;
  };

  const openCalendarModal = () => {
    showCalendarModal.value = true;
  };

  const closeCalendarModal = () => {
    showCalendarModal.value = false;
  };

  const applyRemoteConfig = (data = {}) => {
    disabledTimes.value = data.disabledTimes || {};
    weeklyTemplates.value = data.weeklyTemplates || createDefaultWeeklyTemplates();
    const nextLabels = data.weeklyDayLabels || { ...weeklyDayLabelsBase };
    Object.keys(weeklyTemplates.value).forEach((key) => {
      if (!nextLabels[key]) {
        nextLabels[key] = weeklyDayLabelsBase[key] || key;
      }
    });
    weeklyDayLabels.value = nextLabels;
    ensureSelectedWeeklyDay();
  };

  return {
    disabledTimes,
    selectedDate,
    showScheduleModal,
    showCalendarModal,
    scheduleTab,
    selectedWeeklyDay,
    weeklyTemplates,
    weeklyDayLabels,
    customWeeklyTime,
    newDayLabel,
    editingDayKey,
    editingDayLabel,
    editingWeeklyTime,
    editingWeeklyTimeValue,
    weeklyDayOptions,
    saveScheduleConfig,
    getDayLabel,
    selectWeeklyDay,
    addWeeklyDay,
    startEditDay,
    cancelEditDay,
    saveDayLabel,
    deleteWeeklyDay,
    startEditWeeklyTime,
    cancelEditWeeklyTime,
    saveWeeklyTimeEdit,
    removeWeeklyTime,
    isTimeDisabled,
    toggleTime,
    addCustomWeeklyTime,
    jumpToNextWeekend,
    loadTimesForDate,
    openScheduleModal,
    closeScheduleModal,
    openCalendarModal,
    closeCalendarModal,
    applyRemoteConfig
  };
};
